import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppManagementService } from '../../../../Utils/_services/app-management/app-management.service';
import { DatePipe, NgFor, NgIf, SlicePipe } from '@angular/common';
import { PageTitleService } from '../../../../Utils/_services/page-title/page-title.service';
import { ToastrService, ToastrModule } from 'ngx-toastr';
import { ICustomError } from '../../../../Utils/_models/network-response';
import { AdminNavDetailsComponent } from '../../layout/admin-nav-details/admin-nav-details.component';
import { IAppManagementEntityDto } from '../../../../Utils/_services/app-management/app-management.model';


declare var bootstrap: any;

@Component({
  selector: 'app-app-management',
  standalone: true,
  imports: [FormsModule, NgFor, NgIf, ToastrModule, AdminNavDetailsComponent, DatePipe],
  templateUrl: './app-management.component.html',
  styleUrl: './app-management.component.css'
})
export class AppManagementComponent {

  // properties
  apps: IAppManagementEntityDto[] = [];
  allReleaseOrders: Array<number> = [];
  pageNumber = 1;
  pageSize = 5;
  totalCount = 0;
  totalPages = 0;
  isLoading = false;
  isSubmitting = false;
  isDeleting = false;
  isEditMode = false;
  appToDelete: any = null;
  currentApp: IAppManagementEntityDto = {
    id: "",
    title: '',
    description: '',
    logoUrl: '',
    websiteUrl: '',
    storeUrl: ''
  };

  constructor(
    private appService: AppManagementService,
    private pageTitle: PageTitleService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.pageTitle.setTitle('App Management');
    this.loadApps(this.pageNumber);
  }

  loadApps(page: number): void {
    this.pageNumber = page;
    this.isLoading = true;
    this.appService.getApps(this.pageNumber, this.pageSize).subscribe({
      next: (response) => {
        this.apps = response.data?.items;
        this.totalCount = response.data.totalCount;
        this.allReleaseOrders = Array.from({ length: this.totalCount }, (_, index) => index);
        this.totalPages = this.totalCount && this.pageSize
          ? Math.ceil(this.totalCount / this.pageSize)
          : 1;
        this.isLoading = false;
      },
      error: (err: ICustomError) => {
        this.isLoading = false;
        console.error('Failed to load apps:', err.error.msg);
        this.toastr.error('Error loading apps. Please try again.', 'Error');
      }
    });
  }

  totalPagesArray(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  openEditAppModal(app: any): void {
    this.isEditMode = true;
    this.currentApp = { ...app };
    const modalElement = document.getElementById('appModal');
    if (modalElement && typeof bootstrap !== 'undefined') {
      try {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      } catch (error) {
        console.error('Error opening edit app modal:', error);
      }
    }
  }

  saveApp(): void {
    if (this.isEditMode) {
      this.updateApp();
    } else {
      this.addApp();
    }
  }

  addApp(): void {
    this.isSubmitting = true;
    this.appService.createApp(this.currentApp).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.toastr.success('App created successfully!', 'Success');
        this.closeModal();
        this.loadApps(this.pageNumber);
      },
      error: (err: ICustomError) => {
        this.isSubmitting = false;
        console.error('Add app failed:', err.error.msg);
        this.toastr.error(err.error?.msg || 'Failed to add app. Please try again.', 'Error');
      }
    });
  }

  updateApp(): void {
    this.isSubmitting = true;
    this.appService.updateApp(this.currentApp.id ?? "", this.currentApp).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.toastr.info('App updated successfully!', 'Success');
        this.closeModal();
        this.loadApps(this.pageNumber);
      },
      error: (err: ICustomError) => {
        this.isSubmitting = false;
        console.error('Update app failed:', err.error.msg);
        this.toastr.error(err.error?.msg || 'Failed to update app. Please try again.', 'Error');
      }
    });

    this.appService.clearCached().subscribe({
      next: () => {},
      error: () => {
        this.toastr.error("Failed to clear cache");
      }
    })
  }

  openDeleteConfirmModal(app: any): void {
    this.appToDelete = app;
    const modalElement = document.getElementById('deleteConfirmModal');
    if (modalElement && typeof bootstrap !== 'undefined') {
      try {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      } catch (error) {
        console.error('Error opening delete modal:', error);
        // Fallback: show confirmation dialog
        if (confirm(`Are you sure you want to delete the app "${app.title}"?`)) {
          this.confirmDelete();
        }
      }
    } else {
      // Fallback: show confirmation dialog
      if (confirm(`Are you sure you want to delete the app "${app.title}"?`)) {
        this.confirmDelete();
      }
    }
  }

   openAddAppModal(): void {
    this.isEditMode = false;
    this.resetCurrentApp();
    const modalElement = document.getElementById('appModal');
    if (modalElement && typeof bootstrap !== 'undefined') {
      try {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      } catch (error) {
        console.error('Error opening add app modal:', error);
      }
    }
  }

  confirmDelete(): void {
    if (this.appToDelete) {
      this.isDeleting = true;
      this.appService.deleteApp(this.appToDelete.id).subscribe({
        next: () => {
          this.isDeleting = false;
          this.toastr.warning('App deleted successfully!', 'Success');
          this.loadApps(this.pageNumber);
          this.closeDeleteModal();
        },
        error: (err: ICustomError) => {
          this.isDeleting = false;
          console.error('Delete app failed:', err.error.msg);
          this.toastr.error(err.error?.msg || 'Failed to delete app. Please try again.', 'Error');
        }
      });
    }
  }

  closeDeleteModal(): void {
    const modalElement = document.getElementById('deleteConfirmModal');
    if (modalElement && typeof bootstrap !== 'undefined') {
      try {
        const modalInstance = bootstrap.Modal.getInstance(modalElement);
        if (modalInstance) {
          modalInstance.hide();
        }
      } catch (error) {
        console.error('Error closing delete modal:', error);
        // Fallback: manually hide the modal
        modalElement.style.display = 'none';
        modalElement.classList.remove('show');
        document.body.classList.remove('modal-open');
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          backdrop.remove();
        }
      }
    }
    this.appToDelete = null;
  }

  closeModal(): void {
    const modalElement = document.getElementById('appModal');
    if (modalElement && typeof bootstrap !== 'undefined') {
      try {
        const modalInstance = bootstrap.Modal.getInstance(modalElement);
        if (modalInstance) {
          modalInstance.hide();
        }
      } catch (error) {
        console.error('Error closing modal:', error);
        // Fallback: manually hide the modal
        modalElement.style.display = 'none';
        modalElement.classList.remove('show');
        document.body.classList.remove('modal-open');
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          backdrop.remove();
        }
      }
    }
    this.resetCurrentApp();
  }

  resetCurrentApp(): void {
    this.currentApp = {
      id: "",
      title: '',
      description: '',
      logoUrl: '',
      websiteUrl: '',
      storeUrl: ''
    };
  }

  onReleaseOrderChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;
    this.currentApp.releaseOrder = Number(selectedValue);
  }
}
